import { type JSX } from 'react';

import { consumerApi } from './api';
import { EmptyState, ErrorState, LoadingState, OfferGrid } from './components';
import { SearchBar } from './SearchBar';
import { usePageMeta } from './page-meta';
import { AppLink, buildQuery, useNavigator } from './router';
import { useResource } from './useResource';

/**
 * Search results.
 *
 * ## The URL is the state
 *
 * Every filter lives in the query string, so a result set can be shared,
 * bookmarked and reloaded. Changing a filter **replaces** the history entry
 * rather than pushing one — otherwise Back walks through every checkbox a
 * customer touched instead of leaving the page.
 *
 * ## No ranking happens here
 *
 * The order the BFF returned is the order rendered. Sorting client-side would
 * reorder a page whose cursor was built against the server's order, and results
 * would repeat or vanish between pages.
 */
export function SearchPage(): JSX.Element {
  const { location, replace } = useNavigator();
  const query = location.query;

  const text = query.get('q') ?? '';
  const kind = query.get('kind') ?? '';
  const postcode = query.get('postcode') ?? '';
  const sort = query.get('sort') ?? '';
  const bookableOnly = query.get('bookableOnly') === 'true';
  const maxPrice = query.get('maxPrice') ?? '';

  // The whole query string is the cache key, so a filter change abandons the
  // in-flight request and a slow answer cannot land after a fast one.
  const search = query.toString();
  const resource = useResource(`search?${search}`, (signal) =>
    consumerApi.search(search.length > 0 ? `?${search}` : '', signal),
  );

  usePageMeta({
    title: text.length > 0 ? `${text} — search results` : 'Search the marketplace',
    description:
      'Search pharmacies, clinics and practitioners on Just Treated for medicines, tests and treatments.',
    // Canonical without the query: a search sorted by price is the same page as
    // the same search sorted by relevance.
    canonicalPath: '/search',
    // Results pages are thin and near-infinite in combination. Indexing them
    // competes with the real product and storefront pages.
    noindex: true,
  });

  const update = (changes: Record<string, string | boolean | undefined>): void => {
    const next = new URLSearchParams(search);

    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined || value === '' || value === false) next.delete(key);
      else next.set(key, String(value));
    }

    // A filter change is not a navigation the customer chose to make.
    next.delete('cursor');
    replace(`/search${next.toString().length > 0 ? `?${next.toString()}` : ''}`);
  };

  return (
    <div className="jt-page jt-container">
      <h1 className="jt-page__title">
        {text.length > 0 ? `Results for “${text}”` : 'Search the marketplace'}
      </h1>

      <SearchBar initialQuery={text} initialPostcode={postcode} autoFocus={text.length === 0} />

      <div className="jt-filters" role="group" aria-label="Filter results">
        <div className="jt-filters__field">
          <label htmlFor="jt-filter-kind">Type</label>
          <select
            id="jt-filter-kind"
            className="jt-select"
            value={kind}
            onChange={(event) => update({ kind: event.target.value })}
          >
            <option value="">Everything</option>
            <option value="PRODUCT">Products</option>
            <option value="SERVICE">Services</option>
          </select>
        </div>

        <div className="jt-filters__field">
          <label htmlFor="jt-filter-sort">Sort by</label>
          <select
            id="jt-filter-sort"
            className="jt-select"
            value={sort}
            onChange={(event) => update({ sort: event.target.value })}
          >
            <option value="">Relevance</option>
            <option value="PRICE_ASC">Price: low to high</option>
            <option value="PRICE_DESC">Price: high to low</option>
          </select>
        </div>

        <div className="jt-filters__field">
          <label htmlFor="jt-filter-max-price">Max price (£)</label>
          <input
            id="jt-filter-max-price"
            className="jt-input"
            type="number"
            min={0}
            step={1}
            value={maxPrice === '' ? '' : String(Math.trunc(Number(maxPrice) / 100))}
            onChange={(event) => {
              const pounds = event.target.value.trim();
              // Converted to minor units here because that is what the API
              // takes; the conversion is integer-only, so no float is sent.
              update({
                maxPrice: pounds === '' ? undefined : String(Math.trunc(Number(pounds)) * 100),
                // A band without a currency is refused by the API, so the two
                // always travel together.
                priceCurrency: pounds === '' ? undefined : 'GBP',
              });
            }}
          />
        </div>

        <div className="jt-filters__field jt-filters__field--check">
          <input
            id="jt-filter-bookable"
            type="checkbox"
            checked={bookableOnly}
            onChange={(event) =>
              update({
                bookableOnly: event.target.checked,
                // Bookability is a service concept; the API refuses it against
                // PRODUCT, so the two are kept consistent here.
                kind: event.target.checked ? 'SERVICE' : kind === 'SERVICE' ? '' : kind,
              })
            }
          />
          <label htmlFor="jt-filter-bookable">Only show bookable services</label>
        </div>
      </div>

      {resource.status === 'loading' ? <LoadingState label="Searching…" /> : null}

      {resource.status === 'error' ? (
        <ErrorState message={resource.message} onRetry={resource.reload} />
      ) : null}

      {resource.status === 'loaded' ? (
        resource.data.results.length === 0 ? (
          <EmptyState title="Nothing matched that search.">
            <p>Try fewer words, or remove a filter.</p>
          </EmptyState>
        ) : (
          <>
            <p className="jt-results__count" role="status">
              {resource.data.pageInfo.totalMatched ?? resource.data.results.length} result
              {(resource.data.pageInfo.totalMatched ?? resource.data.results.length) === 1 ? '' : 's'}
            </p>

            <OfferGrid offers={resource.data.results} label="Search results" />

            {resource.data.pageInfo.hasMore && resource.data.pageInfo.nextCursor !== undefined ? (
              <p className="jt-results__more">
                <AppLink
                  className="jt-button jt-button--secondary"
                  to={`/search${buildQuery({
                    ...Object.fromEntries(new URLSearchParams(search)),
                    cursor: resource.data.pageInfo.nextCursor,
                  })}`}
                >
                  Show more results
                </AppLink>
              </p>
            ) : null}
          </>
        )
      ) : null}
    </div>
  );
}
